import type { ActorRefFrom } from 'xstate';
import { assign, setup } from 'xstate';
import { Role } from '~/features/user/types';
import { ALERT_VIEW_STATE, PROVIDER_REGISTRATION_VIEW } from '~/features/xstateSystem';
import { PARSEABLE_EXTENSIONS, toTypesLabel } from '../../vault/documents/constants';
import {
  PARSE_EXTENSIONS_BY_WORKFLOW,
  canParseFile,
  getFileExtension,
} from '../../vault/documents/parseWorkflow';
import { getUnsupportedFileNames, unsupportedFileTypeSubText } from '../../vault/documents/utils';
import { providerRegistrationService } from './registration.service.machine';
import type {
  CVViewContext,
  ParseOutcome,
  ProviderType_,
  TypeAndCategoryValues,
  TypeAndCategoryValuesContext,
  WelcomeValues,
} from './types';

/**
 * Provider Registration View Machine
 *
 * UI ORCHESTRATION MACHINE
 *
 * Responsibilities:
 * - UI state orchestration (which page is active)
 * - Event forwarding to service machine (maps UI events to business events)
 * - Service lifecycle management
 *
 * States represent UI pages:
 * - welcome: Welcome form page
 * - typeCategory: Type & Category/NPI form page
 * - cvUpload: CV upload page
 * - completing: Registration completing, waiting for service to finish
 *
 * Note: This machine does NOT handle business logic or data persistence.
 * All business operations are delegated to the service machine.
 *
 * Lifecycle: Invoked when entering registration view, stopped on exit
 */

export const providerRegistrationView = setup({
  actors: {
    // documentsService: documentsService,
    providerRegistrationService: providerRegistrationService,
  },
  types: {
    context: {} as {
      registrationService?: ActorRefFrom<typeof providerRegistrationService>;
      userInfoId: number; // Store userInfoId for spawning service
      loading: boolean;
      welcomeForm: WelcomeValues;
      typeAndCategoryForm: TypeAndCategoryValuesContext;
      typeOptions: ProviderType_[];
      cvUpload: CVViewContext;
      route: string;
      registered: boolean;
      providerId?: number; // Store providerId from service
    },
    events: {} as  //FIX:
      | { type: 'ON_UPLOAD'; payload: { type: string; files: FileList } }
      // A file was picked (drop or Browse). It is only *staged* -- the doc type
      // question is asked afterwards, so no upload starts here. [MCV-1593]
      | { type: 'ON_UPLOAD_CV'; files: FileList }
      // The user answered the doc type question; this is what starts the upload.
      | { type: 'ON_CLASSIFY_CV'; docType: 'cv' | 'packet' }
      // "Choose a different file" -- discard the staged file, back to the prompt.
      | { type: 'ON_CANCEL_CLASSIFY' }
      //FIX:
      | {
          type: 'ON_NEXT_PAGE';
          goTo: string;
          payload: WelcomeValues | TypeAndCategoryValues | undefined;
        }
      | { type: 'ON_SKIP' }
      | { type: 'ON_SET_PAGE'; goTo: string }
      | { type: 'ON_OPEN_MODAL' }
      | { type: 'ON_CLOSE_MODAL' }
      | { type: 'ON_RESET_ERROR' }
      | { type: 'ON_REGISTRATION_SUBMIT' }
      | { type: 'ON_RESET_CV_ERROR' }
      | { type: 'ON_SET_LOADING'; payload: { loading: boolean } }
      | { type: 'ON_DRAG_ENTER' }
      | { type: 'ON_DRAG_LEAVE' }
      | { type: 'ON_SUBMIT_WELCOME'; data: WelcomeValues }
      | { type: 'ON_FETCH_TYPES'; providerCategoryId: number }
      | { type: 'ON_SUBMIT_TYPE_CATEGORY'; data: TypeAndCategoryValues }
      | { type: 'ON_SET_CATEGORY_OPTION'; id: number }
      | { type: 'ON_COMPLETE_REGISTRATION' }
      | { type: 'ON_PREVIOUS_PAGE' }
      // Events from service (via subscription)
      | { type: 'ON_PROVIDER_CREATED'; providerId: number }
      | { type: 'ON_CV_UPLOADED'; documentReference?: { id: number; docName: string } }
      | { type: 'ON_PARSE_SETTLED'; outcome: ParseOutcome }
      | { type: 'ON_SERVICE_ERROR'; error: Error }
      | { type: 'ON_REGISTRATION_COMPLETE' },
    output: {} as {
      providerId?: number;
      employerUserId?: number;
      roleCode?: Role;
    },
    input: {} as {
      userInfoId: number;
      primaryEmail: string;
    },
  },
}).createMachine({
  id: PROVIDER_REGISTRATION_VIEW,
  initial: '$_WELCOME',
  // [MCV-1937] What the user machine's $_PROVIDER_REGISTRATION onDone receives. It must be
  // the ROOT output: XState 5 ignores an `output` on a top-level final state unless the root
  // maps it, so on $_COMPLETED it left the new provider with no roleCode or providerId until
  // a reload, and the vault's HealthcareLX switch lost its job-opportunities target.
  output: ({ context }) => {
    const snapshot = context.registrationService?.getSnapshot();
    const providerId = snapshot?.context.providerId ?? 0;
    return {
      providerId: providerId,
      employerUserId: undefined,
      roleCode: 'PROVIDER',
    };
  },
  context: ({ input }) => ({
    route: 'welcome',
    registered: false,
    userInfoId: input.userInfoId,
    registrationService: undefined as ActorRefFrom<typeof providerRegistrationService> | undefined,
    loading: false,
    welcomeForm: {
      email: input.primaryEmail,
      phone: undefined,
      mfa: false,
      tosModalOpen: false,
      tosAccepted: false,
    },
    typeOptions: [],
    typeAndCategoryForm: {
      category: null, //TODO:  check schema and reference
      type: null, // TODO: check schema and reference
      firstName: '',
      lastName: '',
      npiNum: undefined,
    },
    cvUpload: {
      dragging: false,
      uploading: false,
      uploaded: false,
      parsing: false,
      parseOutcome: 'pending' as ParseOutcome,
      errorMsg: undefined,
      name: undefined,
      type: undefined,
      size: undefined,
      docType: 'cv',
      documentReference: undefined,
      pendingFile: undefined,
    },
  }),
  states: {
    $_WELCOME: {
      entry: assign({
        registrationService: ({ spawn, self, context }) => {
          return spawn('providerRegistrationService', {
            input: {
              userInfoId: context.userInfoId,
              parentRef: self,
            },
          });
        },
      }),
      on: {
        ON_OPEN_MODAL: {
          actions: assign({
            welcomeForm: ({ context }) => ({ ...context.welcomeForm, tosModalOpen: true }),
          }),
        },
        ON_CLOSE_MODAL: {
          actions: assign({
            welcomeForm: ({ context }) => ({ ...context.welcomeForm, tosModalOpen: false }),
          }),
        },
        ON_SUBMIT_WELCOME: {
          actions: [
            assign({
              welcomeForm: ({ event }) =>
                (event as { type: 'ON_SUBMIT_WELCOME'; data: WelcomeValues }).data,
            }),
          ],
          target: '$_TYPE_AND_CATEGORY',
        },
      },
    },
    $_TYPE_AND_CATEGORY: {
      on: {
        ON_SET_CATEGORY_OPTION: {
          actions: [
            assign({
              typeOptions: ({ event, context }) => {
                const categoryId = (event as { type: 'ON_SET_CATEGORY_OPTION'; id: number }).id;
                const snapshot = context.registrationService?.getSnapshot();
                return snapshot
                  ? snapshot.context.types.filter(
                      (type: ProviderType_) => type.categoryId === categoryId
                    )
                  : [];
              },
            }),
          ],
        },
        ON_SUBMIT_TYPE_CATEGORY: [
          {
            // If the provider was already created on a prior pass through this
            // step, skip the create (the backend rejects duplicates on
            // clerk_user_id) and move straight to CV upload.
            guard: ({ context }) => Boolean(context.providerId),
            actions: assign({
              typeAndCategoryForm: ({ event }) =>
                (event as { type: 'ON_SUBMIT_TYPE_CATEGORY'; data: TypeAndCategoryValues }).data,
            }),
            target: '$_CV_UPLOAD',
          },
          {
            actions: assign({
              typeAndCategoryForm: ({ event }) =>
                (event as { type: 'ON_SUBMIT_TYPE_CATEGORY'; data: TypeAndCategoryValues }).data,
            }),
            target: '$_CREATING_PROVIDER',
          },
        ],
        ON_PREVIOUS_PAGE: {
          target: '$_WELCOME',
        },
      },
    },
    $_CREATING_PROVIDER: {
      entry: ({ context }) => {
        context.registrationService?.send({
          type: 'CREATE_PROVIDER',
          data: {
            typeAndCategory: context.typeAndCategoryForm,
            welcome: context.welcomeForm,
          },
        });
      },
      on: {
        ON_PROVIDER_CREATED: {
          target: '$_CV_UPLOAD',
          actions: [
            assign({
              providerId: ({ event }) => (event as { type: string; providerId: number }).providerId,
            }),
          ],
        },
        ON_SERVICE_ERROR: {
          target: '$_TYPE_AND_CATEGORY',
          actions: [
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Failed to create provider',
                    subText: 'Please try again or contact support if the problem persists.',
                    type: 'error',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    $_CV_UPLOAD: {
      on: {
        ON_RESET_CV_ERROR: {
          actions: assign({
            cvUpload: ({ context }) => ({
              ...context.cvUpload,
              uploading: false,
              uploaded: false,
              errorMsg: undefined,
              dragging: false,
            }),
          }),
        },
        ON_DRAG_ENTER: {
          actions: assign({
            cvUpload: ({ context }) => ({
              ...context.cvUpload,
              dragging: true,
            }),
          }),
        },
        ON_DRAG_LEAVE: {
          actions: assign({
            cvUpload: ({ context }) => ({
              ...context.cvUpload,
              dragging: false,
            }),
          }),
        },
        /**
         * A file was selected -- STAGE it, do not upload. [MCV-1593]
         *
         * The doc type answer is baked into the POST (docCategory, workflow_type
         * and docDesc all derive from it in api.uploadCV), so the question has to
         * precede the request. We stay in $_CV_UPLOAD and gate the classify
         * prompt on context alone; a new top-level state would never render,
         * because Registration.tsx maps states to routes off a hardcoded
         * whitelist and anything unlisted falls through to a bare spinner.
         *
         * `uploading` deliberately stays false -- nothing is in flight yet.
         */
        ON_UPLOAD_CV: [
          // The staged file is the first one picked or dropped, so that is the
          // one screened. Browse is limited by `accept`, but a drop is not, and
          // some pickers offer "All files". An unsupported file is refused here
          // rather than reaching the doc type question and then the server.
          {
            guard: ({ event }) => {
              const file = (event as { type: string; files?: FileList }).files?.[0];
              return (
                file !== undefined &&
                getUnsupportedFileNames([file], PARSEABLE_EXTENSIONS).length > 0
              );
            },
            actions: [
              // A drop fires no `dragleave`, so `dragging` has to clear here too.
              assign({ cvUpload: ({ context }) => ({ ...context.cvUpload, dragging: false }) }),
              ({ event, system }) => {
                const file = (event as { type: string; files: FileList }).files[0];
                try {
                  system?.get(ALERT_VIEW_STATE).send({
                    type: 'ON_SHOW_ALERT',
                    payload: {
                      text: 'Unsupported file type',
                      subText: unsupportedFileTypeSubText([file.name], PARSEABLE_EXTENSIONS),
                      type: 'error',
                      timeout: 6000,
                    },
                  });
                } catch (error) {
                  console.error('[VIEW] Failed to send alert:', error);
                }
              },
            ],
          },
          {
            actions: assign({
              cvUpload: ({ context, event }) => {
                const typed = event as { type: string; files: FileList };
                // Indexing rather than `.item(0)`: this is also reached from the
                // drop handler, whose FileList the specs mock with a plain array.
                const file = typed.files?.[0];
                // A drop carries no files at all when the user drags text, a URL
                // or an image out of another page onto the zone. `dragging` still
                // has to clear -- the drop happened, and no `dragleave` follows it
                // (MCV-1590) -- but there is nothing to stage or ask about.
                // handleFileInputChange guards its own path already; the drop
                // handler forwards every event, so the guard belongs here where it
                // covers both senders.
                if (!file) {
                  return {
                    ...context.cvUpload,
                    dragging: false,
                  };
                }
                return {
                  ...context.cvUpload,
                  // Keep the File itself, never the FileList: `dataTransfer.files`
                  // goes into protected mode once the drop handler returns.
                  pendingFile: file,
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  // A drop fires no `dragleave`, so ON_DRAG_LEAVE never arrives
                  // and `dragging` would stay latched, shadowing everything that
                  // follows in UploadPrompt (MCV-1590).
                  dragging: false,
                  errorMsg: undefined,
                };
              },
            }),
          },
        ],
        /**
         * The user answered the doc type question -- now the upload starts.
         *
         * ORDER IS LOAD-BEARING: xstate v5 runs actions in sequence and later
         * actions observe earlier assigns, so the send must read
         * `context.cvUpload.pendingFile` BEFORE the assign clears it. Flipping
         * these two lines ships `file: undefined` into the FormData with no
         * runtime error anywhere.
         */
        ON_CLASSIFY_CV: [
          // The pick gate accepts either parse list, since the file is picked before this
          // question. The answer narrows it to that workflow's list (MCV-1760): a .png is a
          // packet, not a CV. The file stays staged so the user can answer the other way or
          // choose another file. The API enforces the same rule; this is the fast feedback.
          {
            guard: ({ context, event }) => {
              const file = context.cvUpload.pendingFile;
              const { docType } = event as { type: string; docType: 'cv' | 'packet' };
              return (
                file !== undefined &&
                !canParseFile(docType === 'packet' ? 'packet-parse' : 'cv-parse', file.name)
              );
            },
            actions: ({ context, event, system }) => {
              const file = context.cvUpload.pendingFile;
              const { docType } = event as { type: string; docType: 'cv' | 'packet' };
              const workflow = docType === 'packet' ? 'packet-parse' : 'cv-parse';
              const ext = getFileExtension(file?.name ?? '').toUpperCase();
              try {
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Unsupported file type',
                    subText: `${ext ? `${ext} files` : 'This file'} cannot be parsed as a ${docType === 'packet' ? 'credentialing packet' : 'CV'}. Supported: ${toTypesLabel(PARSE_EXTENSIONS_BY_WORKFLOW[workflow])}.`,
                    type: 'error',
                    timeout: 6000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          },
          {
            actions: [
              ({ context, event }) => {
                const typed = event as { type: string; docType: 'cv' | 'packet' };
                const file = context.cvUpload.pendingFile;
                if (!file) return;
                context.registrationService?.send({
                  type: 'UPLOAD_CV',
                  file,
                  docType: typed.docType,
                });
              },
              assign({
                cvUpload: ({ context, event }) => ({
                  ...context.cvUpload,
                  docType: (event as { type: string; docType: 'cv' | 'packet' }).docType,
                  uploading: true,
                  // Cleared the instant the upload begins so the classify branch
                  // cannot out-rank the spinner in UploadPrompt (MCV-1590).
                  pendingFile: undefined,
                  dragging: false,
                }),
              }),
            ],
            target: '$_UPLOADING_CV',
          },
        ],
        // "Choose a different file" -- drop the staged file and its metadata so
        // the default prompt (which owns the Browse Files input) comes back.
        ON_CANCEL_CLASSIFY: {
          actions: assign({
            cvUpload: ({ context }) => ({
              ...context.cvUpload,
              pendingFile: undefined,
              name: undefined,
              type: undefined,
              size: undefined,
            }),
          }),
        },
        /**
         * Skipping the step abandons a staged file. Without this clear it
         * survives in context, and any path that restores $_CV_UPLOAD (notably
         * $_COMPLETING's ON_SERVICE_ERROR) re-asks the doc type question about a
         * file the user already walked away from. [MCV-1593]
         *
         * This is the ONLY exit that can carry a staged file, which is why it is
         * the only one that clears. ON_COMPLETE_REGISTRATION is sent solely from
         * CVUploadPage.handleNext under `if (documentReference)`, and once a
         * document reference exists CVUploadForm returns the success card, which
         * has neither drop zone nor Browse Files input -- so nothing can be
         * staged on that path. `cannot stage a file once the upload has produced
         * a document` in cv-classify-on-upload.spec.tsx pins that invariant; if
         * it breaks, the clear has to come back here too.
         */
        ON_NEXT_PAGE: {
          actions: [
            ({ context }) => {
              context.registrationService?.send({ type: 'CREATE_USER_ROLE' });
            },
            assign({
              cvUpload: ({ context }) => ({
                ...context.cvUpload,
                pendingFile: undefined,
                name: undefined,
                type: undefined,
                size: undefined,
              }),
            }),
          ],
          target: '$_COMPLETING',
        },
        // No staged-file clear here on purpose: see ON_NEXT_PAGE above. This
        // event is only ever sent once a document reference exists, and that
        // state has no way to stage a file.
        ON_COMPLETE_REGISTRATION: {
          actions: ({ context }) => {
            context.registrationService?.send({ type: 'CREATE_USER_ROLE' });
          },
          target: '$_COMPLETING',
        },
        ON_PREVIOUS_PAGE: [
          {
            // Once a provider is created, type/category can't be updated via
            // the existing API — prevent navigating back to that step.
            guard: ({ context }) => Boolean(context.providerId),
          },
          {
            target: '$_TYPE_AND_CATEGORY',
          },
        ],
      },
    },
    $_UPLOADING_CV: {
      on: {
        ON_CV_UPLOADED: {
          // The file is stored but the AI parse has only been queued. Stay on a
          // parsing state so the indicator keeps running until it settles.
          target: '$_PARSING_CV',
          actions: [
            assign({
              cvUpload: ({ context, event }) => ({
                ...context.cvUpload,
                uploading: false,
                uploaded: true,
                parsing: true,
                parseOutcome: 'pending' as ParseOutcome,
                dragging: false,
                errorMsg: undefined,
                documentReference: (
                  event as { type: string; documentReference?: { id: number; docName: string } }
                ).documentReference,
              }),
            }),
            ({ context, system }) => {
              try {
                const isPacket = context.cvUpload.docType === 'packet';
                const label = isPacket ? 'Credentialing Packet' : 'CV';
                // `label.toLowerCase()` rendered "Your cv is being processed."
                const subject = isPacket ? 'credentialing packet' : 'CV';
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: `${label} uploaded successfully`,
                    subText: `Your ${subject} is still being processed.`,
                    type: 'success',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
        ON_SERVICE_ERROR: {
          target: '$_CV_UPLOAD',
          actions: [
            // Without this the upload flags stay latched and the user is left
            // on a spinner that never resolves; the warning alert below is the
            // only sign anything went wrong.
            assign({
              cvUpload: ({ context }) => ({
                ...context.cvUpload,
                uploading: false,
                uploaded: false,
                parsing: false,
                dragging: false,
              }),
            }),
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE).send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Failed to upload CV',
                    subText: 'You can continue without uploading or try again.',
                    type: 'warning',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    /**
     * Upload stored, AI parse queued. The service machine polls it and reports
     * exactly one ON_PARSE_SETTLED, so this state always terminates. The user is
     * free to leave -- Next/Back/Complete are all live here.
     */
    $_PARSING_CV: {
      on: {
        ON_PARSE_SETTLED: {
          target: '$_CV_UPLOAD',
          actions: assign({
            cvUpload: ({ context, event }) => ({
              ...context.cvUpload,
              parsing: false,
              parseOutcome: (event as { type: string; outcome: ParseOutcome }).outcome,
            }),
          }),
        },
        ON_NEXT_PAGE: {
          actions: ({ context }) => {
            context.registrationService?.send({ type: 'CREATE_USER_ROLE' });
          },
          target: '$_COMPLETING',
        },
        ON_COMPLETE_REGISTRATION: {
          actions: ({ context }) => {
            context.registrationService?.send({ type: 'CREATE_USER_ROLE' });
          },
          target: '$_COMPLETING',
        },
        ON_PREVIOUS_PAGE: [
          {
            guard: ({ context }) => Boolean(context.providerId),
          },
          {
            target: '$_TYPE_AND_CATEGORY',
          },
        ],
        ON_SERVICE_ERROR: {
          target: '$_CV_UPLOAD',
          actions: assign({
            cvUpload: ({ context }) => ({
              ...context.cvUpload,
              parsing: false,
              parseOutcome: 'unknown' as ParseOutcome,
            }),
          }),
        },
      },
    },
    $_COMPLETING: {
      on: {
        ON_REGISTRATION_COMPLETE: {
          target: '$_COMPLETED',
        },
        // This transition restores the CV page, and it does so with no staged
        // file: every entry into $_COMPLETING already guarantees that ($_CV_UPLOAD's
        // ON_NEXT_PAGE clears it, its ON_COMPLETE_REGISTRATION cannot have one,
        // and $_PARSING_CV's exits are past the staging step), and nothing inside
        // $_COMPLETING can set it. A clear here would be a provable no-op. [MCV-1593]
        ON_SERVICE_ERROR: {
          target: '$_CV_UPLOAD',
          actions: [
            ({ system }) => {
              try {
                system?.get(ALERT_VIEW_STATE)?.send({
                  type: 'ON_SHOW_ALERT',
                  payload: {
                    text: 'Registration completion failed',
                    subText: 'Please try again.',
                    type: 'error',
                    timeout: 5000,
                  },
                });
              } catch (error) {
                console.error('[VIEW] Failed to send alert:', error);
              }
            },
          ],
        },
      },
    },
    $_COMPLETED: {
      type: 'final' as const,
    },
  },
});
